import { CalendarDays, Check, Infinity } from "lucide-react";
import { Link } from "react-router";

import { formatPrice } from "@/site/billing/config";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";

import { buildBillingHref } from "./navigation";
import type { CoursePricingData } from "./types";

export function CoursePricingPlans({
  pricing,
  returnTo,
}: {
  pricing: CoursePricingData;
  returnTo?: string | null;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <section className="border-primary/40 bg-card flex h-full flex-col rounded-md border p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-xl font-semibold">Lifetime</h3>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">Pay once</Badge>
            <Infinity className="text-success size-5" />
          </div>
        </div>
        <div className="mt-5">
          <span className="text-4xl font-semibold">
            {formatPrice(pricing.lifetimePriceCents)}
          </span>
          <span className="text-muted-foreground ml-2 text-sm">one-time</span>
        </div>
        <FeatureList
          features={[
            "Full learning access forever",
            "All current and future lessons and concepts",
            "No subscription to manage",
          ]}
        />
        <div className="mt-auto pt-6">
          <Button asChild className="w-full">
            <Link to={buildBillingHref({ plan: "lifetime", returnTo })}>
              Buy lifetime access
            </Link>
          </Button>
        </div>
      </section>

      <section className="bg-card flex h-full flex-col rounded-md border p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-xl font-semibold">30-day pass</h3>
          <CalendarDays className="text-success size-5" />
        </div>
        <div className="mt-5">
          <span className="text-4xl font-semibold">
            {formatPrice(pricing.unitPriceCents)}
          </span>
          <span className="text-muted-foreground ml-2 text-sm">one-time</span>
          <p className="text-muted-foreground mt-2 font-mono text-xs">
            1 pass = {pricing.passDurationDays} days of full access ·{" "}
            {pricing.lifetimeUnitCount} total passes = lifetime access
          </p>
        </div>
        <FeatureList
          features={[
            `Full learning access for ${pricing.passDurationDays} days`,
            `Every pass counts toward the ${formatPrice(
              pricing.lifetimePriceCents,
            )} lifetime total`,
            "No subscription or automatic renewal",
          ]}
        />
        <div className="mt-auto pt-6">
          <Button asChild className="w-full" variant="outline">
            <Link to={buildBillingHref({ plan: "pass", returnTo })}>
              Start a 30-day pass
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

function FeatureList({ features }: { features: readonly string[] }) {
  return (
    <ul className="mt-5 grid gap-2 text-sm">
      {features.map((feature) => (
        <li key={feature} className="flex items-center gap-2">
          <Check className="text-success size-4" />
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  );
}
